import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { formatRp } from '@/lib/format';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, WalletCards } from 'lucide-react';

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

export default function AgiBalanceDialog({ open, onOpenChange, pid, systemBalance = 0, current, onSaved }) {
  const [bankBalance, setBankBalance] = useState('');
  const [cashBalance, setCashBalance] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBankBalance(current?.bank_balance ?? '');
    setCashBalance(current?.cash_balance ?? '');
  }, [open, current]);

  const total = useMemo(() => number(bankBalance) + number(cashBalance), [bankBalance, cashBalance]);
  const difference = total - Number(systemBalance || 0);
  const hasInput = bankBalance !== '' || cashBalance !== '';
  const differenceClass = difference < 0 ? 'text-[var(--fpccs-danger)]' : 'text-[var(--fpccs-success)]';

  const submit = async (event) => {
    event.preventDefault();
    if (!hasInput) { toast.error('Isi saldo rekening atau saldo cash terlebih dahulu'); return; }
    if (number(bankBalance) < 0 || number(cashBalance) < 0) { toast.error('Saldo tidak boleh minus'); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${pid}/actual-balances`, {
        bank_balance: number(bankBalance),
        cash_balance: number(cashBalance),
      });
      toast.success('Saldo aktual Agi diperbarui');
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal memperbarui saldo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="agi-balance-dialog" className="max-w-lg bg-[var(--fpccs-surface-1)] border-[var(--fpccs-border)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WalletCards className="w-5 h-5 text-[var(--fpccs-primary)]" /> Update Saldo Agi</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="rounded-lg border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] p-3">
            <div className="text-[11px] text-subtle-fpccs">Sisa Saldo Sistem Agi</div>
            <div className="font-mono text-lg font-semibold">{formatRp(systemBalance)}</div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Saldo Rekening (Rp)</Label>
            <Input type="number" min="0" autoFocus data-testid="agi-bank-balance" value={bankBalance} onChange={(e) => setBankBalance(e.target.value)} placeholder="cth: 35000000" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Saldo Cash (Rp)</Label>
            <Input type="number" min="0" data-testid="agi-cash-balance" value={cashBalance} onChange={(e) => setCashBalance(e.target.value)} placeholder="cth: 35000000" />
          </div>
          <div className="flex items-end justify-between gap-4 rounded-lg bg-[var(--fpccs-surface-2)] p-3">
            <div>
              <div className="text-[11px] text-subtle-fpccs">Total Saldo Aktual</div>
              <div className="font-mono text-2xl font-semibold">{formatRp(total)}</div>
              {hasInput && <div className={`mt-1 text-xs font-mono ${differenceClass}`}>{formatRp(difference)} {difference === 0 ? '(sesuai)' : ''}</div>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-agi-balance" className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Saldo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}